import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  blankPayee,
  paymentPayFrom,
  spendingAccounts,
  validatePayee,
  type PayeeDraft,
  type PayeeKind,
} from '@tahi/core';
import { addPayee, editPayee } from '../../../data/commands/payments';
import { useCommandEnv, useUsable } from '../../../data/hooks';
import { ScreenHeader, showToast } from '../../../ui';
import { useAccounts, useBudgetSettings, useFunds } from '../../budget/hooks';
import { useCategories, useGoBack } from '../../spending/hooks';
import { SCREEN } from '../../spending/styles';
import { PayeeForm } from '../components/PayeeForm';
import { copy, errorText } from '../copy';
import { kindLabel } from '../format';
import { PAYMENTS_BASE, usePaymentsSettings, usePaymentsSnapshot, useToday } from '../hooks';

const KINDS: PayeeKind[] = ['bill', 'utility', 'card', 'loan'];

/** /m/payments/new?kind=bill and /m/payments/p/:id/edit */
export function PayeeFormRoute() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const env = useCommandEnv();
  const usable = useUsable();
  const navigate = useNavigate();
  const today = useToday();
  const snap = usePaymentsSnapshot();
  const categories = useCategories() ?? [];
  const funds = useFunds();
  const accounts = useAccounts();
  const budgetSettings = useBudgetSettings();
  const paySettings = usePaymentsSettings();
  const goBack = useGoBack(id ? `${PAYMENTS_BASE}/p/${id}` : PAYMENTS_BASE);
  const [error, setError] = useState<string | null>(null);

  const kindParam = params.get('kind') as PayeeKind | null;
  const existing = id ? snap?.payees.find((p) => p.id === id) : undefined;
  if ((id && !snap) || !paySettings) return null;
  if (id && !existing) return <p className="text-muted">{copy.notFound}</p>;
  const kind = existing?.kind ?? (kindParam && KINDS.includes(kindParam) ? kindParam : 'bill');
  const initial: PayeeDraft = existing ?? blankPayee(kind, today, paySettings.remindDays);
  // v0.7.4: the account a payee without its own pays from (Payments' default, else Budget's).
  const fallbackPayFrom = paymentPayFrom(
    null,
    null,
    paySettings,
    budgetSettings ?? { useFunds: false, defaultAccountId: null },
    true,
    accounts ?? [],
  );

  async function submit(draft: PayeeDraft) {
    if (existing) {
      const r = await editPayee(env, existing.id, draft);
      if (!r.ok) return setError(errorText(r.error));
      showToast({ message: 'Saved' });
      goBack();
      return;
    }
    const r = await addPayee(env, draft);
    if (!r.ok) return setError(errorText(r.error));
    showToast({ message: `${draft.name || draft.lender} added` });
    navigate(`${PAYMENTS_BASE}/p/${r.id}`, { replace: true });
  }

  return (
    <div className={`${SCREEN} max-w-xl`}>
      <ScreenHeader
        title={existing ? `${copy.edit} ${existing.name}` : `Add ${kindLabel[kind].toLowerCase()}`}
        back={
          existing
            ? { to: `${PAYMENTS_BASE}/p/${existing.id}`, label: existing.name }
            : { to: PAYMENTS_BASE, label: copy.title }
        }
      />
      <PayeeForm
        key={existing?.id ?? kind}
        initial={initial}
        mode={existing ? 'edit' : 'add'}
        categories={categories.filter((c) => !c.hidden)}
        funds={usable('budget') && budgetSettings?.useFunds ? (funds ?? []) : null}
        accounts={usable('budget') ? spendingAccounts(accounts ?? [], existing?.accountId) : null}
        payFromName={accounts?.find((a) => a.id === fallbackPayFrom)?.name ?? null}
        error={error}
        validate={validatePayee}
        onSubmit={submit}
        onCancel={goBack}
      />
    </div>
  );
}
